import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { permissionMiddleware } from "~/lib/permission-middleware";

// ============================================================================
// Types
// ============================================================================

export interface FeeExemptionRow {
  id: string;
  coachId: string;
  coachName: string;
  coachEmail: string;
  reason: string;
  startDate: Date | null;
  endDate: Date | null;
  isActive: boolean;
  createdAt: Date | null;
}

export interface CoachOption {
  id: string;
  fullName: string;
  email: string;
}

// ============================================================================
// Query Keys
// ============================================================================

export const feeExemptionQueryKeys = {
  all: ["fee-exemptions"] as const,
  coachSearch: (search: string) =>
    ["fee-exemptions", "coach-search", search] as const,
};

// ============================================================================
// Server Functions
// ============================================================================

/**
 * List all fee exemptions with coach info, ordered by creation date desc.
 */
export const getFeeExemptionsFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:read")])
  .handler(async (): Promise<FeeExemptionRow[]> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachFeeExemptions, coaches } = await import("~/../drizzle/schema");
    const { eq, desc } = await import("drizzle-orm");

    const db = await getDBWithInit();

    const rows = await db
      .select({
        id: coachFeeExemptions.id,
        coachId: coachFeeExemptions.coachId,
        coachName: coaches.fullName,
        coachEmail: coaches.email,
        reason: coachFeeExemptions.reason,
        startDate: coachFeeExemptions.startDate,
        endDate: coachFeeExemptions.endDate,
        isActive: coachFeeExemptions.isActive,
        createdAt: coachFeeExemptions.createdAt,
      })
      .from(coachFeeExemptions)
      .innerJoin(coaches, eq(coachFeeExemptions.coachId, coaches.id))
      .orderBy(desc(coachFeeExemptions.createdAt));

    return rows;
  });

export const feeExemptionsQueryOptions = queryOptions({
  queryKey: feeExemptionQueryKeys.all,
  queryFn: () => getFeeExemptionsFn(),
});

/**
 * Search coaches by name or email for the add-exemption form.
 */
export const searchCoachesFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:read")])
  .validator(z.object({ search: z.string().min(1) }))
  .handler(async ({ data }): Promise<CoachOption[]> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coaches } = await import("~/../drizzle/schema");
    const { or, ilike } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const term = `%${data.search}%`;

    const results = await db
      .select({
        id: coaches.id,
        fullName: coaches.fullName,
        email: coaches.email,
      })
      .from(coaches)
      .where(or(ilike(coaches.fullName, term), ilike(coaches.email, term)))
      .limit(10);

    return results;
  });

/**
 * Create a new fee exemption.
 */
export const createFeeExemptionFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:write")])
  .validator(
    z.object({
      coachId: z.string().uuid(),
      reason: z.string().min(1),
      startDate: z.coerce.date().optional(),
      endDate: z.coerce.date().nullable().optional(),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachFeeExemptions } = await import("~/../drizzle/schema");

    const db = await getDBWithInit();

    await db.insert(coachFeeExemptions).values({
      coachId: data.coachId,
      reason: data.reason,
      startDate: data.startDate ?? new Date(),
      endDate: data.endDate ?? null,
      isActive: true,
    });

    return { success: true };
  });

/**
 * Toggle isActive or update endDate for a fee exemption.
 */
export const updateFeeExemptionFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:write")])
  .validator(
    z.object({
      exemptionId: z.string().uuid(),
      isActive: z.boolean().optional(),
      endDate: z.coerce.date().nullable().optional(),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachFeeExemptions } = await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();

    const updates: Record<string, unknown> = {};
    if (data.isActive !== undefined) updates.isActive = data.isActive;
    if (data.endDate !== undefined) updates.endDate = data.endDate;

    await db
      .update(coachFeeExemptions)
      .set(updates)
      .where(eq(coachFeeExemptions.id, data.exemptionId));

    return { success: true };
  });

/**
 * Delete a fee exemption.
 */
export const deleteFeeExemptionFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:write")])
  .validator(
    z.object({
      exemptionId: z.string().uuid(),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coachFeeExemptions } = await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();

    await db
      .delete(coachFeeExemptions)
      .where(eq(coachFeeExemptions.id, data.exemptionId));

    return { success: true };
  });
